import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoice, faPenToSquare, faReceipt } from '@fortawesome/free-solid-svg-icons';
import { Loader } from '../../Widgets';
import { NewCustomer } from '../../Widgets/NewCustomer';
import ArthaGateway from 'service/ArthaGateway';
import { durations, getCurrentFinancialYearDuration } from 'service/Utils';
import { Party, Transaction } from 'service';
import { RootState } from 'store/store';
import { ActionCreators } from 'store/actionCreators';

const CustomerProfilePage = () => {
    const appData = useSelector((state: RootState) => state.appdata);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [durationId, setDurationId] = useState<string>(getCurrentFinancialYearDuration());
    const [customer, setCustomer] = useState<Party | null>(null);
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [transactionFilter, setTransactionFilter] = useState<string>('all');
    const [showTransactionDetails, setShowTransactionDetails] = useState<boolean>(false);
    const [showEdit, setShowEdit] = useState<boolean>(false);
    const [pageError, setPageError] = useState<string | null>(null);

    const locationState = location.state as { customer?: Party } | undefined;

    useEffect(() => {
        const routeCustomerId = parseInt(id || '0');
        const selectedParty = appData?.party as Party | null;
        const stateCustomer = locationState?.customer || null;

        if (stateCustomer && stateCustomer.id === routeCustomerId) {
            setCustomer(stateCustomer);
            setIsLoading(false);
            return;
        }

        if (selectedParty && selectedParty.id === routeCustomerId) {
            setCustomer(selectedParty);
            setIsLoading(false);
            return;
        }

        setPageError('Customer not found in current session. Please open customer profile from the customer list.');
        setIsLoading(false);
    }, [id, appData, locationState]);

    useEffect(() => {
        if (!customer || customer.id <= 0) {
            return;
        }

        setIsLoading(true);
        setPageError(null);

        ArthaGateway
            .getTransactions(customer.accountId, durationId, 1, 100)
            .then((data: any) => {
                setTransactions(data?.entries || []);
                setIsLoading(false);
            })
            .catch((error: any) => {
                const message = error?.response?.data?.message || 'Unable to load transactions right now.';
                setPageError(message);
                setIsLoading(false);
            });
    }, [customer, durationId]);

    const onCustomerSaved = (savedCustomer: Party) => {
        setCustomer(savedCustomer);
        dispatch(ActionCreators.selectCustomer(savedCustomer));
        setShowEdit(false);
        toast.success('Customer details updated');
    };

    const createNewInvoice = () => {
        if (!customer) {
            return;
        }
        dispatch(ActionCreators.selectCustomer(customer));
        navigate('/invoice/new');
    };

    const createNewReceipt = () => {
        if (!customer) {
            return;
        }
        dispatch(ActionCreators.selectCustomer(customer));
        navigate('/receipt/new');
    };

    const transactionSummary = useMemo(() => {
        let invoiceCount = 0;
        let receiptCount = 0;
        let invoiceAmount = 0;
        let receiptAmount = 0;

        transactions.forEach((transaction) => {
            const voucherType = (transaction.voucherType || transaction.transactionType || '').toLowerCase();
            if (voucherType.includes('sale') || voucherType.includes('invoice')) {
                invoiceCount += 1;
                invoiceAmount += (transaction.debit || 0);
            }
            if (voucherType.includes('receipt')) {
                receiptCount += 1;
                receiptAmount += (transaction.credit || 0);
            }
        });

        return {
            invoiceCount,
            receiptCount,
            invoiceAmount,
            receiptAmount
        };
    }, [transactions]);

    const visibleTransactions = useMemo(() => {
        if (transactionFilter === 'all') {
            return transactions;
        }

        return transactions.filter((transaction) => {
            const voucherType = (transaction.voucherType || transaction.transactionType || '').toLowerCase();
            if (transactionFilter === 'invoice') {
                return voucherType.includes('sale') || voucherType.includes('invoice');
            }
            return voucherType.includes('receipt');
        });
    }, [transactions, transactionFilter]);

    const currencyFormat = (value: number) =>
        new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(value || 0);

    const getTransactionAmount = (transaction: Transaction) => {
        if ((transaction.credit || 0) > 0) {
            return transaction.credit || 0;
        }
        return transaction.debit || 0;
    };

    const isReceiptTransaction = (transaction: Transaction) => {
        const voucherType = (transaction.voucherType || transaction.transactionType || '').toLowerCase();
        return voucherType.includes('receipt');
    };

    if (isLoading) {
        return (
            <div className='page-customer-profile app-page-shell'>
                <Loader isFullHeight={true} />
            </div>
        );
    }

    return (
        <div className='page-customer-profile app-page-shell'>
            <div className='container'>
                <div className='customer-profile-hero mb-3'>
                    <div>
                        <div className='hero-label'>Customer Profile</div>
                        <h2>{customer?.name || '--'}</h2>
                        <p>{customer?.mobileNumber || '--'} | {customer?.email || 'No email added'}</p>
                    </div>
                    <div className='hero-actions'>
                        <button type='button' className='btn btn-outline-light btn-sm' onClick={() => setShowEdit(!showEdit)}>
                            <FontAwesomeIcon icon={faPenToSquare} />
                            <span className='ms-1'>Edit Details</span>
                        </button>
                        <button type='button' className='btn btn-light btn-sm' onClick={createNewInvoice}>
                            <FontAwesomeIcon icon={faFileInvoice} />
                            <span className='ms-1'>New Invoice</span>
                        </button>
                        <button type='button' className='btn btn-light btn-sm' onClick={createNewReceipt}>
                            <FontAwesomeIcon icon={faReceipt} />
                            <span className='ms-1'>New Receipt</span>
                        </button>
                    </div>
                </div>

                {showEdit && customer &&
                    <NewCustomer
                        initialParty={customer}
                        onCustomerSaved={onCustomerSaved}
                        onClose={() => setShowEdit(false)}
                    />
                }

                {pageError &&
                    <div className='alert alert-danger mb-3' role='alert'>
                        {pageError}
                    </div>
                }

                {/*{customer &&
                    <div className='app-panel mb-3'>
                        <div className='row g-2'>
                            <div className='col-6 col-md-3'>
                                <div className='summary-card'>
                                    <div className='label'>Invoices</div>
                                    <div className='value'>{transactionSummary.invoiceCount}</div>
                                </div>
                            </div>
                            <div className='col-6 col-md-3'>
                                <div className='summary-card'>
                                    <div className='label'>Receipts</div>
                                    <div className='value'>{transactionSummary.receiptCount}</div>
                                </div>
                            </div>
                            <div className='col-6 col-md-3'>
                                <div className='summary-card'>
                                    <div className='label'>Invoice Value</div>
                                    <div className='value'>{currencyFormat(transactionSummary.invoiceAmount)}</div>
                                </div>
                            </div>
                            <div className='col-6 col-md-3'>
                                <div className='summary-card'>
                                    <div className='label'>Receipt Value</div>
                                    <div className='value'>{currencyFormat(transactionSummary.receiptAmount)}</div>
                                </div>
                            </div>
                        </div>

                        <div className='customer-meta-grid mt-3'>
                            <div>
                                <span className='meta-label'>Address</span>
                                <div className='meta-value'>
                                    {customer.billingAddress1 || '--'}
                                    {customer.billingAddress2 ? <><br />{customer.billingAddress2}</> : ''}
                                    {customer.billingCity ? <><br />{customer.billingCity}</> : ''}
                                    {customer.billingPinCode ? <><br />{customer.billingPinCode}</> : ''}
                                </div>
                            </div>
                            <div>
                                <span className='meta-label'>GSTIN</span>
                                <div className='meta-value'>{customer.gstin || '--'}</div>
                            </div>
                            <div>
                                <span className='meta-label'>PAN</span>
                                <div className='meta-value'>{customer.pan || '--'}</div>
                            </div>
                        </div>
                    </div>
                }*/}

                <div className='app-panel'>
                    <div className='app-page-header mb-2'>
                        <h2>Transactions</h2>
                        <p>Invoices and receipts for this customer.</p>
                    </div>
                    <div className='app-toolbar'>
                        <div>
                            <label className='form-label'>Duration</label>
                            <select className='form-select' value={durationId} onChange={(e) => setDurationId(e.target.value)}>
                                {durations.map((duration) => (
                                    <option key={duration.value} value={duration.value}>{duration.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className='form-label'>Type</label>
                            <select className='form-select' value={transactionFilter} onChange={(e) => setTransactionFilter(e.target.value)}>
                                <option value='all'>All</option>
                                <option value='invoice'>Invoices</option>
                                <option value='receipt'>Receipts</option>
                            </select>
                        </div>
                        <div className='transaction-detail-toggle'>
                            <label className='form-label d-none d-md-block'>&nbsp;</label>
                            <div className='form-check form-switch mb-0'>
                                <input
                                    className='form-check-input'
                                    type='checkbox'
                                    role='switch'
                                    checked={showTransactionDetails}
                                    onChange={(e) => setShowTransactionDetails(e.target.checked)}
                                />
                                <label className='form-check-label'>Show details</label>
                            </div>
                        </div>
                    </div>

                    {visibleTransactions.length === 0 &&
                        <div className='app-empty-state'>
                            No transactions found for selected filters.
                        </div>
                    }

                    {visibleTransactions.length > 0 &&
                        <div className='transaction-list'>
                            {visibleTransactions.map((transaction, index) => (
                                <div className='transaction-card' key={index}>
                                    <div className='d-flex justify-content-between align-items-start gap-2'>
                                        <div>
                                            <div className='transaction-title'>{transaction.voucherNumber || transaction.transactionRefNumber}</div>
                                            <div className='transaction-subtitle'>
                                                {transaction.voucherType || transaction.transactionType} | {moment(transaction.transactionDate).format('DD/MM/YYYY')}
                                            </div>
                                        </div>
                                        <div className={'transaction-amount ' + (isReceiptTransaction(transaction) ? 'receipt' : 'invoice')}>
                                            {currencyFormat(getTransactionAmount(transaction))}
                                        </div>
                                    </div>
                                    {showTransactionDetails &&
                                        <>
                                            <div className='transaction-balance-subtle mt-1'>
                                                Closing balance: {currencyFormat(transaction.closingBalance || 0)}
                                            </div>
                                            {transaction.description &&
                                                <div className='transaction-note mt-2'>
                                                    {transaction.description}
                                                </div>
                                            }
                                        </>
                                    }
                                </div>
                            ))}
                        </div>
                    }
                </div>
            </div>
        </div>
    );
};

export const CustomerProfile = () => {
    return <CustomerProfilePage />;
};
